import { useState, useEffect } from 'react';
import { getLogger } from '@confluence/logger';

const logger = getLogger('highlight-action');

const TRELLO_HAS_ACCESS_ENDPOINT = '/wiki/rest/trello/1.0/has-access';

export interface TrelloAccessState {
	hasTrelloAccess: boolean;
	loading: boolean;
	error: boolean;
}

/**
 * Hook to check if the current user has Trello access.
 * Uses the Identity Platform permissions service to verify the user has
 * both 'use/site' and 'write' permissions on Trello.
 *
 * @returns TrelloAccessState - { hasTrelloAccess, loading, error }
 */
export const useTrelloAccess = (): TrelloAccessState => {
	const [state, setState] = useState<TrelloAccessState>({
		hasTrelloAccess: false,
		loading: true,
		error: false,
	});

	useEffect(() => {
		let mounted = true;

		const checkTrelloAccess = async () => {
			try {
				const response = await fetch(TRELLO_HAS_ACCESS_ENDPOINT, {
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
					},
				});

				if (!mounted) {
					return;
				}

				if (!response.ok) {
					logger.warn`Failed to check Trello access: status=${response.status}`;
					setState({
						hasTrelloAccess: false,
						loading: false,
						error: true,
					});
					return;
				}

				const data = (await response.json()) as { hasTrelloAccess?: boolean };
				setState({
					hasTrelloAccess: Boolean(data.hasTrelloAccess),
					loading: false,
					error: false,
				});
			} catch (error) {
				if (!mounted) {
					return;
				}
				logger.error`Error checking Trello access: ${error}`;
				setState({
					hasTrelloAccess: false,
					loading: false,
					error: true,
				});
			}
		};

		void checkTrelloAccess();

		return () => {
			mounted = false;
		};
	}, []);

	return state;
};
